import Ionicons from "@expo/vector-icons/Ionicons";
import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Button } from "@/components/ui/button";
import { Screen } from "@/components/ui/screen";
import { Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { authStrings as strings } from "@/i18n/features/auth.i18n";
import { notify } from "@/lib/dialog";
import { useAuthStore } from "@/state/auth-store";

/**
 * Shown when the session is valid but we could not load who the user is.
 *
 * This screen exists because the honest answer was previously unavailable in
 * the model. A valid session plus a `/users/me` that failed for any non-401
 * reason was published as `authenticated` with `user: null`, and
 * `needsProfileSetup(null)` is `true` — so the router sent a fully signed-up
 * person into onboarding and asked them to re-enter a profile they already had.
 * That is what a Cloudflare Access interception looked like from the inside,
 * and a captive portal looks the same.
 *
 * Two things it deliberately does NOT do:
 *
 *  - **It does not sign anyone out.** The credentials are almost certainly
 *    fine; the network is not. Clearing them would turn a refresh into a
 *    re-login, and on this screen we cannot even tell the user why.
 *  - **It does not pretend to be a profile problem.** Retry is the primary
 *    action because "try again when the connection is back" is the true
 *    remedy. Sign out is offered, quietly, for the case where a user is stuck
 *    and wants a way out.
 */
export default function AccountUnavailable() {
  const { t } = useTranslation();
  const c = useTheme();
  const hydrate = useAuthStore((s) => s.hydrate);
  const signOut = useAuthStore((s) => s.signOut);

  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // `hydrate` re-runs the whole bootstrap: it re-reads storage, re-fetches
  // /users/me, and sets the status itself. On success the routing hook moves
  // us off this screen, so there is nothing to navigate to by hand.
  const retry = async () => {
    setBusy(true);
    try {
      await hydrate();
    } finally {
      setBusy(false);
    }
  };

  // Sign-out is server-confirmed like everywhere else: it clears the local
  // session only if the server revokes it. When the API is unreachable — often
  // why this screen is showing — it fails and the user stays signed in, so
  // Retry (reconnect) remains the real way off this screen.
  const signOutNow = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      notify(t("errors.generic"), t("errors.tryAgainInAMoment"));
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Screen>
      <View style={styles.body}>
        <Ionicons name="cloud-offline-outline" size={48} color={c.textMuted} />
        <ThemedText variant="title" center>
          {t(strings.unavailableTitle)}
        </ThemedText>
        <ThemedText tone="muted" center>
          {t(strings.unavailableBody)}
        </ThemedText>
      </View>

      <View style={styles.actions}>
        <Button
          label={t(strings.tryAgain)}
          onPress={retry}
          loading={busy}
          disabled={busy}
          fullWidth
        />
        <Button
          label={t(strings.unavailableSignOut)}
          variant="ghost"
          onPress={signOutNow}
          loading={signingOut}
          disabled={busy || signingOut}
          fullWidth
        />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  body: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.md,
  },
  actions: { gap: Spacing.sm, paddingBottom: Spacing.lg },
});
